import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { GET_VENDOR_TRANSACTIONS, GET_TRANSACTION } from '../graphql/queries';
import Layout from '../components/Layout';
import { useAuth } from '../utils/authContext';
import { formatDateTime } from '../utils/dateFormat';
import {
    ChevronLeft,
    ChevronRight,
    AlertCircle,
    Loader,
    Info,
    Plus
} from 'lucide-react';
import { FaFilter } from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import TransactionDetails from './TransactionDetails';
import ManualTransactionForm from '../components/ManualTransactionForm';

const TransactionStatus = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    SUCCESS: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800',
    CANCELLED: 'bg-gray-100 text-gray-800'
};

const SettlementStatus = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    PROCESSING: 'bg-blue-100 text-blue-800',
    COMPLETED: 'bg-green-100 text-green-800',
    AUTOMATIC: 'bg-green-100 text-green-800',
    FAILED: 'bg-red-100 text-red-800'
};

const PaymentMethodBadge = {
    WHATSAPP: 'bg-green-100 text-green-800',
    PAYSTACK: 'bg-blue-100 text-blue-800',
    // Add other payment methods as needed
};

const Transactions = () => {
    const { user } = useAuth();
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        status: '',
        startDate: '',
        endDate: '',
        paymentMethod: '',
    });
    const [page, setPage] = useState(1);
    const ITEMS_PER_PAGE = 10;
    const [selectedTransactionReference, setSelectedTransactionReference] = useState(null);
    const [showManualTransactionForm, setShowManualTransactionForm] = useState(false);

    const { loading, error, data, refetch } = useQuery(GET_VENDOR_TRANSACTIONS, {
        variables: {
            catalogId: user?.selectedCatalog?.id,
            status: filters.status || null,
            startDate: filters.startDate || null,
            endDate: filters.endDate || null,
            paymentMethod: filters.paymentMethod || null,
            productType: filters.productType || null,
            page,
            limit: ITEMS_PER_PAGE,
        },
        skip: !user?.selectedCatalog?.id,
    });


    // Add a separate query for detailed transaction info
    const { data: transactionDetails, loading: loadingDetails } = useQuery(GET_TRANSACTION, {
        variables: {
            reference: selectedTransactionReference
        },
        skip: !selectedTransactionReference,
    });

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
        setPage(1);
    };

    const formatCustomerInfo = (customer) => {
        if (!customer) return 'Anonymous Customer';
        if (!customer.name && !customer.phone) return 'Anonymous Customer';
        if (!customer.name) return `Customer (${customer.phone})`;
        if (!customer.phone) return customer.name;
        return `${customer.name}`;
    };

    const openTransactionDetails = (reference) => {
        setSelectedTransactionReference(reference);
    };

    const closeTransactionDetails = () => {
        setSelectedTransactionReference(null);
    };

    const handleManualTransactionSuccess = () => {
        setShowManualTransactionForm(false);
        // Refetch transactions to update the list
        refetch();
    };

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="flex items-center gap-2 p-4 text-red-700 bg-red-100 rounded-md">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error.message}</span>
                </div>
            </Layout>
        );
    }

    const transactions = data?.getVendorTransactions?.edges || [];
    const totalCount = data?.getVendorTransactions?.totalCount || 0;
    const successfulTransactions = data?.getVendorTransactions?.successfulTransactions || 0;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    // Get the transaction details for the modal
    const transaction = transactionDetails?.getTransaction;

    return (
        <Layout title="Transactions" description={"View and manage your transactions."}>
            <div className="p-4 pb-12">
                {/* <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">

                    <button
                        onClick={() => setShowManualTransactionForm(true)}
                        className="flex items-center justify-center gap-2 bg-[#191970] text-white px-4 py-2 rounded-lg hover:bg-[#191950] transition-colors"
                    >
                        <Plus size={18} />
                        Add Manual Transaction
                    </button>
                </div> */}

                {/* Settlement Overview Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {/* Primary metric - what they earned (hero card) */}
                    <div className="bg-gradient-to-tr from-green-600 to-green-800 p-4 rounded-lg shadow text-white">
                        <h3 className="text-sm font-medium opacity-90">Your Total Earnings</h3>
                        <p className="text-2xl font-bold">₦{data?.getVendorTransactions?.totalVendorEarnings?.toLocaleString() || '0'}</p>
                        <p className="text-xs opacity-80 mt-1">Automatically sent to your account</p>
                    </div>

                    {/* Business growth metric */}
                    <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                        <h3 className="text-sm font-medium text-gray-600">Total Sales Volume</h3>
                        <p className="text-xl font-semibold text-blue-600">₦{data?.getVendorTransactions?.totalSales?.toLocaleString() || '0'}</p>
                        <p className="text-xs text-gray-500 mt-1">Your business growth</p>
                    </div>

                    {/* Performance metric - builds confidence */}
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-sm font-medium text-gray-600">Successful Orders</h3>
                        <p className="text-xl font-semibold text-green-600">
                            {successfulTransactions}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">Happy customers served</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-4">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center justify-center gap-2 w-full sm:w-auto mb-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
                    >
                        <FaFilter className="text-gray-500" />
                        {showFilters ? 'Hide Filters' : 'Show Filters'}
                    </button>

                    {showFilters && (
                        <div className="bg-white p-4 rounded-lg shadow mb-4 ">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        className="w-full form-select rounded-md p-2.5 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                    >
                                        <option value="">All statuses</option>
                                        <option value="PENDING">Pending</option>
                                        <option value="SUCCESS">Success</option>
                                        <option value="FAILED">Failed</option>
                                        <option value="CANCELLED">Cancelled</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                                    <select
                                        className="w-full form-select rounded-md p-2.5 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                        value={filters.paymentMethod}
                                        onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                                    >
                                        <option value="">All methods</option>
                                        <option value="WHATSAPP">WhatsApp</option>
                                        <option value="PAYSTACK">Paystack</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
                                    <select
                                        className="w-full form-select rounded-md p-2.5 bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                        value={filters.productType}
                                        onChange={(e) => handleFilterChange('productType', e.target.value)}
                                    >
                                        <option value="">All types</option>
                                        <option value="digital">Digital Only</option>
                                        <option value="physical">Physical Only</option>
                                        <option value="mixed">Mixed</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                    <input
                                        type="date"
                                        className="w-full form-input rounded-md border-gray-300 p-2.5 bg-white focus:border-blue-500 focus:ring-blue-500"
                                        value={filters.startDate}
                                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                    <input
                                        type="date"
                                        className="w-full form-input rounded-md border-gray-300 p-2.5 bg-white focus:border-blue-500 focus:ring-blue-500"
                                        value={filters.endDate}
                                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    />
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        onClick={() => setFilters({ status: '', startDate: '', endDate: '', paymentMethod: '', productType: '' })}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Mobile Cards (visible on small screens) - With overflow fix */}
                <div className="sm:hidden grid grid-cols-1 gap-4">
                    {transactions.length > 0 ? (
                        transactions.map(({ node }) => (
                            <div key={node.id} className="bg-white p-4 rounded-lg shadow">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${node.status === 'SUCCESS' ? 'bg-green-500' :
                                                node.status === 'PENDING' ? 'bg-yellow-500' : 'bg-red-500'
                                                }`}></span>
                                            <p className="font-medium truncate">{node.reference}</p>

                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(node.dateCreated)}</p>
                                    </div>

                                    {/* Digital indicator */}
                                    {node.digitalOnly && (
                                        <span className="mr-1 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-md">
                                            Digital
                                        </span>
                                    )}

                                    <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${PaymentMethodBadge[node.paymentMethod] || 'bg-gray-100 text-gray-800'}`}>
                                        {node.paymentMethod || 'Unknown'}
                                    </span>
                                </div>

                                <div className="grid grid-cols-2 gap-y-2 mt-3 pt-2 border-t border-gray-100">
                                    <div>
                                        <p className="text-xs text-gray-500">Customer</p>
                                        <p className="text-sm font-medium truncate">{formatCustomerInfo(node.customer)}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-xs text-gray-500">Amount</p>
                                        <p className="text-sm font-semibold">₦{node.finalAmount.toLocaleString()}</p>
                                    </div>
                                    <div className="col-span-2 text-right">
                                        <p className="text-xs text-gray-500">You receive</p>
                                        <p className="text-sm font-semibold text-green-600">₦{node.vendorAmount ? node.vendorAmount.toLocaleString() : '...'}</p>
                                    </div>
                                </div>

                                <button
                                    onClick={() => openTransactionDetails(node.reference)}
                                    className="w-full mt-3 flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                                >
                                    <Info className="w-3.5 h-3.5" /> Details
                                </button>
                            </div>
                        ))
                    ) : (
                        <div className="bg-white p-6 rounded-lg shadow text-center">
                            <p className="text-gray-500">No transactions found</p>
                        </div>
                    )}
                </div>

                {/* Table (visible on larger screens) */}
                <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">You Receive</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {transactions.map(({ node }) => (
                                <tr key={node.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{node.reference}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatCustomerInfo(node.customer)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">₦{node.finalAmount.toLocaleString()}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-green-600 font-medium">₦{node.vendorAmount ? node.vendorAmount.toLocaleString() : '...'}</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${node.status === 'SUCCESS' ? 'bg-green-500' :
                                                node.status === 'PENDING' ? 'bg-yellow-500' : 'bg-red-500'
                                                }`}></span>
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-md ${PaymentMethodBadge[node.paymentMethod] || 'bg-gray-100 text-gray-800'}`}>
                                                {node.paymentMethod || 'Unknown'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{formatDateTime(node.dateCreated)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                                        <button
                                            onClick={() => openTransactionDetails(node.reference)}
                                            className="inline-flex items-center px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100"
                                        >
                                            <Info className="w-3.5 h-3.5 mr-1" />
                                            Details
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="mt-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <span className="text-sm text-gray-600 text-center sm:text-left">
                        Showing {Math.min(ITEMS_PER_PAGE * (page - 1) + 1, totalCount)} - {Math.min(ITEMS_PER_PAGE * page, totalCount)} of {totalCount}
                    </span>
                    <div className="flex justify-center gap-2">
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Previous
                        </button>
                        <button
                            onClick={() => setPage(p => p + 1)}
                            disabled={page >= totalPages}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                            <ChevronRight className="w-4 h-4 ml-1" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Transaction Details Modal */}
            {selectedTransactionReference && (
                loadingDetails ? (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white p-8 rounded-lg shadow-xl">
                            <LoadingSpinner />
                            <p className="mt-4 text-gray-600">Loading transaction details...</p>
                        </div>
                    </div>
                ) : (
                    <TransactionDetails
                        transaction={transaction}
                        onClose={closeTransactionDetails}
                    />
                )
            )}

            {/* Manual Transaction Form */}
            {showManualTransactionForm && (
                <ManualTransactionForm
                    isOpen={showManualTransactionForm}
                    onSuccess={handleManualTransactionSuccess}
                    onCancel={() => setShowManualTransactionForm(false)}
                />
            )}
        </Layout>
    );
};

export default Transactions;